import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';

const AddProduct = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    showPrice: true, // Default to show price
    whatsappUrl: ''
    // NOTE: no images field in payload anymore
  });

  // NEW: store selected files + preview URLs (no base64)
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const isAdminLoggedIn = localStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/admin');
    }
  }, [navigate]);

  const base = process.env.REACT_APP_API_URL;

  // Handle text input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'price' ? Number(value) : value)
    }));
  };

  // Handle image pick (no base64, just files + object URLs)
  const handleImageChange = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);

    // Limit to 5 images total
    if (imageFiles.length + files.length > 5) {
      setError('You can upload a maximum of 5 images');
      return;
    }

    setImageFiles(prev => [...prev, ...files]);
    const previews = files.map(f => URL.createObjectURL(f));
    setImagePreview(prev => [...prev, ...previews]);
  };

  // Remove a selected image
  const removeImage = (index) => {
    // revoke URL to avoid memory leak
    URL.revokeObjectURL(imagePreview[index]);

    const nextFiles = [...imageFiles];
    nextFiles.splice(index, 1);
    setImageFiles(nextFiles);

    const nextPreviews = [...imagePreview];
    nextPreviews.splice(index, 1);
    setImagePreview(nextPreviews);
  };

  // Helper: upload 1 file to /api/upload -> returns Blob URL
  const uploadOne = async (productId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${base}/api/upload?productId=${productId}`, {
      method: 'POST',
      body: fd
    });
    const data = await res.json();
    if (!res.ok || !data.fileUrl) throw new Error(data.error || 'Upload failed');
    return data.fileUrl;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate form
    if (!formData.name || !formData.description || !formData.whatsappUrl || formData.price === '') {
      setError('Please fill out all required fields');
      setLoading(false);
      return;
    }

    try {
      // 1) Create product WITHOUT images
      const createRes = await fetch(`${base}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const created = await createRes.json();
      if (!createRes.ok || !created.success) {
        throw new Error(created.message || 'Failed to add product');
      }
      const productId = created.data._id;

      // 2) If any images selected, upload them
      let uploadedUrls = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const url = await uploadOne(productId, imageFiles[i]);
        uploadedUrls.push(url);
      }

      // 3) Set main image (first) via /api/products/:id/image
      if (uploadedUrls[0]) {
        const saveMain = await fetch(`${base}/api/products/${productId}/image`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl: uploadedUrls[0] })
        });
        if (!saveMain.ok) throw new Error('Failed to save main image');
      }

      // 4) Save gallery URLs into images (optional, keeps your gallery UI)
      if (uploadedUrls.length > 0) {
        const saveGallery = await fetch(`${base}/api/products/${productId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ images: uploadedUrls })
        });
        const sg = await saveGallery.json();
        if (!saveGallery.ok || !sg.success) {
          throw new Error(sg.message || 'Failed to save gallery images');
        }
      }

      setSuccess('Product added successfully!');
      // Reset form & previews
      imagePreview.forEach(u => URL.revokeObjectURL(u));
      setImageFiles([]);
      setImagePreview([]);
      setFormData({
        name: '',
        description: '',
        price: '',
        showPrice: true,
        whatsappUrl: ''
      });
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Add product error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <AdminSidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-2xl font-bold text-gray-900 font-playfair">Add New Product</h1>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50 p-4">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-sm text-red-700 font-satoshi">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <p className="text-sm text-green-700 font-satoshi">{success}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Product Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 font-satoshi">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                    required
                  />
                </div>
                
                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 font-satoshi">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                    required
                  />
                </div>
                
                {/* Price */}
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 font-satoshi">
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                    required
                  />
                </div>

                {/* Price Display Option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-satoshi mb-3">
                    Price Display Option *
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="showPrice"
                        name="showPrice"
                        type="radio"
                        checked={formData.showPrice === true}
                        onChange={() => setFormData({...formData, showPrice: true})}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <label htmlFor="showPrice" className="ml-3 block text-sm text-gray-900 font-satoshi">
                        Show Price (Display the actual price)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="requestPrice"
                        name="showPrice"
                        type="radio"
                        checked={formData.showPrice === false}
                        onChange={() => setFormData({...formData, showPrice: false})}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <label htmlFor="requestPrice" className="ml-3 block text-sm text-gray-900 font-satoshi">
                        Request for Price (Show "Request for price" with WhatsApp link)
                      </label>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 font-satoshi">
                    Choose whether to display the actual price or show "Request for price" option
                  </p>
                </div>
                
                {/* WhatsApp URL */}
                <div>
                  <label htmlFor="whatsappUrl" className="block text-sm font-medium text-gray-700 font-satoshi">
                    WhatsApp URL *
                  </label>
                  <input
                    type="url"
                    id="whatsappUrl"
                    name="whatsappUrl"
                    value={formData.whatsappUrl}
                    onChange={handleChange}
                    placeholder="https://wa.me/91XXXXXXXXXX"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 font-satoshi">
                    Format: https://wa.me/91XXXXXXXXXX (include country code)
                  </p>
                </div>
                
                {/* Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-satoshi">
                    Product Images (Maximum 5)
                  </label>
                  <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="images"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none"
                        >
                          <span className="font-satoshi">Upload images</span>
                          <input
                            id="images"
                            name="images"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageChange}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1 font-satoshi">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500 font-satoshi">
                        PNG, JPG, GIF up to 5MB each
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Image Previews */}
                {imagePreview.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                    {imagePreview.map((src, index) => (
                      <div key={index} className="relative">
                        <img
                          src={src}
                          alt={`Preview ${index + 1}`}
                          className="h-32 w-full object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 m-1"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-satoshi ${
                      loading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Adding Product...' : 'Add Product'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AddProduct;
